import React, { useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
// Types
import { TicketEventStatus } from 'api/graphql-global-types';
import { PublishMode } from 'pages/Events/components/TicketEventForm/types';
// Hooks
import { useUploadTicketEventImage } from 'pages/Events/components/TicketEventForm/useUploadTicketEventImage';
import { useTicketSaleStatus } from 'pages/Events/hooks/useTicketSaleStatus';
import {
  CreatedTicketEvent,
  useCreateTicketEvent,
} from './hooks/useCreateTicketEvent';
// Helpers
import { buildCreateTicketEventInput } from 'pages/Events/components/TicketEventForm/helpers';
import { getTicketingErrorMessage } from 'pages/Events/helpers';
import { getCreateFormValues } from './helpers';
// Constants
import { EVENTS, TICKET_EVENT_DETAILS_GEN } from 'constants/routes';
// UI
import {
  errorNotification,
  successNotification,
  warningNotification,
} from 'ui/Notification';
// Components
import TicketEventForm, {
  TicketEventFormSubmit,
} from 'pages/Events/components/TicketEventForm/TicketEventForm';

const CreateTicketEvent = (): JSX.Element => {
  const history = useHistory();
  const { search } = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const uploadImage = useUploadTicketEventImage();
  const { approveZeroTax, setStatus } = useTicketSaleStatus();
  const createTicketEvent = useCreateTicketEvent();

  const initialValues = useMemo(
    () =>
      getCreateFormValues(
        new URLSearchParams(search).get('hostId') || undefined
      ),
    [search]
  );

  const handleSubmit = async ({
    values,
    timezone,
    coverImage,
  }: TicketEventFormSubmit): Promise<void> => {
    setSubmitting(true);
    let created: CreatedTicketEvent | null = null;

    try {
      const coverImageFileKey = coverImage
        ? await uploadImage(coverImage)
        : null;
      created = await createTicketEvent(
        buildCreateTicketEventInput(values, { timezone, coverImageFileKey })
      );
    } catch (error) {
      setSubmitting(false);
      errorNotification(
        getTicketingErrorMessage(error, 'Failed to create the event')
      );
      return;
    }

    // The event exists from here on, so any failure lands on its page instead of risking a duplicate
    try {
      if (values.publishMode === PublishMode.Publish) {
        await setStatus(created, TicketEventStatus.OnSale, {
          approveZeroTaxFirst: values.approveZeroTax,
        });
        successNotification('The event is published and on sale');
      } else {
        if (values.approveZeroTax) {
          await approveZeroTax(created);
        }
        successNotification('The event is saved as a draft');
      }
    } catch (error) {
      warningNotification(
        `The event was saved as a draft, but ${
          values.publishMode === PublishMode.Publish
            ? 'publishing'
            : 'approving the tax policy'
        } failed: ${getTicketingErrorMessage(
          error,
          'unknown error'
        )} Finish it from the event page.`
      );
    }

    history.push(`${TICKET_EVENT_DETAILS_GEN}/${created.id}`);
  };

  return (
    <TicketEventForm
      mode="create"
      initialValues={initialValues}
      submitting={submitting}
      onSubmit={handleSubmit}
      onCancel={() => history.push(EVENTS)}
    />
  );
};

export default CreateTicketEvent;
